import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Col, Row, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useCompanyStore } from 'stores';
// icons
import { AiOutlineEye } from 'react-icons/ai';
//helpers
import { verificateNullValue } from 'helpers';
// components
import CustomInput from 'components/backoffice/customInput';
import Button from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import WatchModal from 'components/backoffice/modals/WatchModal';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const BookStoreForm = ({
  isEditing,
  onSubmitForm,
  fileInputRef,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  beforeTitle,
}) => {
  const { code: companyCode } = useCompanyStore();
  const [loading, setLoading] = useState(false);
  const [openModalImage, setOpenModalImage] = useState(false);
  const { t } = useTranslation(['backoffice']);
  const [previewImage, setPreviewImage] = useState(null);
  const {
    formState: { errors },
    getValues,
    setValue,
    watch,
    control,
    handleSubmit,
  } = useForm();
  const logoValue = watch('logo');
  
  const getImageForModal = () => {
    if (previewImage) return previewImage;
    if (typeof logoValue === 'string') return logoValue;
    if (logoValue instanceof Blob || logoValue instanceof File) {
      return URL.createObjectURL(logoValue);
    }
    return '';
  };

  const watchItem = () => {
    setOpenModalImage(true);
  };

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  let item;
  const image = getValues('logo');

  if (isEditing) {
    item =
      typeof image === 'string'
        ? image
        : image instanceof Blob || image instanceof File
          ? URL.createObjectURL(image)
          : '';
  } else {
    item = image !== undefined ? URL.createObjectURL(image) : '';
  }

  const hasValidDefaultValues = (values) => {
    return Object.values(values || {}).some(
      (v) => v !== null && v !== undefined && v !== '' && !(typeof v === 'object' && Object.keys(v || {}).length === 0),
    );
  };

  useEffect(() => {
    if (!hasValidDefaultValues(defaultValues)) {
      return;
    }
    if (isEditing) {
      setValue('logo', null);
      if (defaultValues?.logo)
        fetch(defaultValues.logo)
          .then((response) => response.blob())
          .then((blob) => {
            const name = defaultValues.logo.split('?')[0].split('/').pop();
            const file = new File([blob], name, { type: `image/${name.split('.')[1]}` });
            setValue('logo', file);
          })
          .catch((error) => {
            console.error('Error al obtener la imagen:', error);
          });
    } else {
      setValue('logo', defaultValues.logo);
    }
    setValue('name', verificateNullValue(defaultValues.name));
    setValue('code', verificateNullValue(defaultValues.code));
    setValue('email', verificateNullValue(defaultValues.email));
    setValue('direccion', verificateNullValue(defaultValues.direccion));
    setValue('telefono', defaultValues.telefono !== '' ? parseInt(defaultValues.telefono?.replace(/\s/g, '')) : '');
    setValue('descripcion_pub', defaultValues.descripcion_pub !== '' ? defaultValues.descripcion_pub : '');
    setValue('latitude', verificateNullValue(defaultValues.latitude));
    setValue('longitude', verificateNullValue(defaultValues.longitude));
    setValue('business_hours', verificateNullValue(defaultValues.business_hours));
    setValue('facebook', verificateNullValue(defaultValues.redes_sociales?.facebook));
    setValue('twitter', defaultValues.redes_sociales?.twitter);
    setValue('instagram', defaultValues.redes_sociales?.instagram);
    setValue('google_play', defaultValues.apps_urls?.google_play);
    setValue('app_store', defaultValues.apps_urls?.app_store);
    setValue('commission_for_instore_pickup', defaultValues.commission_for_instore_pickup);
    setValue('shipping_commission', defaultValues.shipping_commission);
  }, [defaultValues]);

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)} role="form">
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} xl={3} className="mb-md-2 mb-lg-0">
            <Controller
              name="name"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('nombre')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('nombre')}
                />
              )}
            />
            {errors?.name && <small className="text-danger ">{errors.name.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: t('incorrectFormat'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="email"
                  value={value}
                  label={t('correoelectronico')}
                  placeholder={t('correoelectronico')}
                  onChange={(e) => onChange(e)}
                  defaultValue={value}
                />
              )}
            />
            {errors?.email && <small className="text-danger">{errors.email.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="code"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                maxLength: {
                  value: 4,
                  message: t('maxlengthcode'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('codigo')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('codigo')}
                />
              )}
            />
            {errors?.code && <small className="text-danger">{errors.code.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="direccion"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('direccion')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('direccion')}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} xl={3} className="mb-md-2 mb-lg-0">
            <Controller
              name="telefono"
              control={control}
              rules={{
                pattern: {
                  value: /^[0-9\b]+$/,
                  message: t('solonumeros'),
                },
                maxLength: {
                  value: 9,
                  message: t('max9characters'),
                },
                minLength: {
                  value: 9,
                  message: t('min9characters'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="tel"
                  label={t('telefono')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('telefono')}
                />
              )}
            />
            {errors?.telefono && <small className="text-danger">{errors.telefono.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="descripcion_pub"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('description')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('description')}
                />
              )}
            />
            {errors?.descripcion_pub && <small className="text-danger ">{errors.descripcion_pub.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="facebook"
              rules={{
                pattern: {
                  value: /^(ftp|http|https):\/\/[^ "]+$/,
                  message: t('urlvalid'),
                },
              }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('facebook')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('facebook')}
                />
              )}
            />
            {errors?.facebook && <small className="text-danger">{errors.facebook.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="twitter"
              rules={{
                pattern: {
                  value: /^(ftp|http|https):\/\/[^ "]+$/,
                  message: t('urlvalid'),
                },
              }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('twitter')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('twitter')}
                />
              )}
            />
            {errors?.twitter && <small className="text-danger">{errors.twitter.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} xl={3} className="mb-md-2 mb-lg-0">
            <Controller
              name="instagram"
              rules={{
                pattern: {
                  value: /^(ftp|http|https):\/\/[^ "]+$/,
                  message: t('urlvalid'),
                },
              }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('instagram')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('instagram')}
                />
              )}
            />
            {errors?.instagram && <small className="text-danger">{errors.instagram.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="latitude"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('latitude')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('latitude')}
                />
              )}
            />
            {errors?.latitude && <small className="text-danger ">{errors.latitude.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="longitude"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('longitude')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('longitude')}
                />
              )}
            />
            {errors?.longitude && <small className="text-danger ">{errors.longitude.message}</small>}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="business_hours"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('openinghours')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('openinghours')}
                  max={105}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={12} md={12} lg={12} xl={6} className="mb-md-2 mb-lg-0 d-flex gap-3">
            <div className="w-100">
              <Controller
                name="logo"
                rules={{
                  required: {
                    value: true,
                    message: t('camporequerido'),
                  },
                }}
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <CustomInput
                      type="file"
                      dropFileMsg={t('dropFile')}
                      label={t('logo')}
                      placeholder={t('selectImg')}
                      accept="image/*"
                      value={value}
                      onChange={(e) => {
                        const file = e instanceof File ? e : e?.target?.files?.[0];
                        if (file) {
                          setPreviewImage(URL.createObjectURL(file));
                        } else {
                          setPreviewImage(null);
                        }
                        onChange(e);
                      }}
                      ref={fileInputRef}
                    />
                  );
                }}
              />
              <p className="color-text-gray">*{t('onlyallowed')}</p>
              {errors?.logo && <small className="text-danger mt-0">{errors.logo.message}</small>}
            </div>
            <div className="mt-4-3">
              <Button type="button" color="terciary" onClick={watchItem}>
                <AiOutlineEye color="white" />
              </Button>
            </div>
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="commission_for_instore_pickup"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                pattern: {
                  value: /^[0-9]+$/,
                  message: t('notDecimalsNumber'),
                },
                max: {
                  value: 100,
                  message: t('maximunValue'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="number"
                  disabled={false}
                  label={t('commissionForInstorePickup')}
                  placeholder={t('commissionForInstorePickup')}
                  onChange={(e) => onChange(e)}
                  classNameLabel="text-light"
                  min={0}
                  max={100}
                  value={value}
                />
              )}
            />
            {errors?.commission_for_instore_pickup && (
              <small className="text-danger">{errors.commission_for_instore_pickup.message}</small>
            )}
          </Col>
          <Col sm={12} md={12} lg={12} xl={3} className="mt-4 mt-xl-0 mb-md-2 mb-lg-0">
            <Controller
              name="shipping_commission"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
                pattern: {
                  value: /^[0-9]+$/,
                  message: t('notDecimalsNumber'),
                },
                max: {
                  value: 100,
                  message: t('maximunValue'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="number"
                  disabled={false}
                  label={t('shippingCommission')}
                  placeholder={t('shippingCommission')}
                  onChange={(e) => onChange(e)}
                  classNameLabel="text-light"
                  min={0}
                  max={100}
                  value={value}
                />
              )}
            />
            {errors?.shipping_commission && <small className="text-danger">{errors.shipping_commission.message}</small>}
          </Col>
        </Row>
        <div className="my-4 d-flex justify-content-end gap-3">
          <Button color="primary" onClick={handleSubmit(onSubmitForm)} loading={loading}>
            {t('save')}
            <SpinnerComponent loading={loading} />
          </Button>
          {isEditing && defaultValues?.code !== companyCode && (
            <Button color="secondary" onClick={() => setOpenModalDelete(true)} loading={loading}>
              {t('eliminar')}
            </Button>
          )}
        </div>
      </Form>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deletebookstore')}
        beforeTitle={t('edit', { title: defaultValues?.name })}
      />
      <WatchModal
        item={getImageForModal()}
        title={t('imagen')}
        show={openModalImage}
        setShow={setOpenModalImage}
        beforeTitle={defaultValues ? t('edit', { title: defaultValues?.name }) : beforeTitle}
      />
    </div>
  );
};

BookStoreForm.propTypes = {
  isEditing: PropTypes.bool,
  onSubmitForm: PropTypes.func,
  fileInputRef: PropTypes.object,
  onDelete: PropTypes.func,
  openModalDelete: PropTypes.bool,
  setOpenModalDelete: PropTypes.func,
  defaultValues: PropTypes.object,
  beforeTitle: PropTypes.string,
};

export default BookStoreForm;